import { useEffect, useRef } from 'react';
import { ActionPerformed, PushNotificationSchema,PushNotifications, Token } from '@capacitor/push-notifications';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Logger } from '../support/Logger';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { DeviceInfo } from '../support/DeviceInfo';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AppState } from '../InnerhiveApp';
import { IInAppNotificationButton, InAppNotificationPresenter } from './InAppNotificationPresenter';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { APP_FOREGROUNDED } from '../AppContainer';

export const PUSH_NOTIFICATION_NAVIGATING = 'PushNotificationListener.navigating';

interface IAPPushNotificationExtras {
    // eg 'Appointment Reminder'
    type: string;

    // eg 'userId/caremaps/12345/notes/67890' to specify entity to reload before navigating
    resourceToReload?: string,

    // eg /notes/12345  to navigate after reloading entity (or reloading app if no resourceToReload specified)
    gotoAndReload?: string;

    // eg /caregiver to navigate without any reload
    goto?: string;

    // '0' for never auto dismiss
    // '30000' for dismiss after 30s
    durationMS?: string;

    // to not show toast, eg a campaign to drive people into the app
    // should be ignored for someone that is actively using the app
    // b/c we don't want to risk interupting what they are doing (eg in
    // the middle of mission creation)
    doNotShowToast?: string; // 'true' | undefined

    // this gets sent to mix panel
    campaignId?: string;

    // not used in app
    userId: string;
    device: string;
}

export const PushNotificationListener: React.FC = () => {

    const logger = useInstance(Logger);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const events = useInstance(BufferedEventBus);

    const history = useInstance(HistoryViewModel);

    const deviceInfo = useInstance(DeviceInfo);

    const apiClient = useInstance(APIClient);

    const appState = useInstance(AppState);

    const registrationErrors = useRef<string[]>([]);

    const inAppNotification = useInstance(InAppNotificationPresenter);

    // save device token to dynamo
    const persistTokenWithRetry = async (token: string, attempt = 1) => {
        try {
            const entry = await apiClient.entry();

            // because user can use the app anonymously
            if (!entry.links.pushNotificationDevices) {
                return;
            }

            const { name, manufacturer, model } = deviceInfo;
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const device = { token, description: `${name}/${manufacturer}/${model}`, timeZone };
            await apiClient.put(entry.links.pushNotificationDevices, device);
            logger.info('Persisted device token');
        } catch (e: any) {
            if (attempt < 5) {
                logger.info('Retrying persist device token: ' + e.message);
                setTimeout(() => persistTokenWithRetry(token, attempt + 1), 10000);
            } else {
                e.message = `Failed to persist device token after 5 attempts: ${e.message}`;
                logger.error(e);
            }
        }
    };

    const reloadApp = (url: string) => {
        window.location.href = `${window.location.pathname}?t=${Date.now()}#/${url}`;
    };

    const maybeNavigate = (url?: string) => {
        if (url && history.getCurrentLocation().pathname !== url) {
            history.push(url);
        }
    };

    useEffect(() => {

        if (!['ios', 'android'].includes(deviceInfo.platform)) {
            // capacitor plugin doesn't support web
            return;
        }

        PushNotifications.addListener('registration',
            async (token: Token) => {
                logger.info('Push notifications registration success, token: ' + token.value);
                registrationErrors.current = [];

                // save device token to dynamo
                await persistTokenWithRetry(token.value);
            }
        );

        PushNotifications.addListener('registrationError',
            ({ error }: { error: string }) => {
                registrationErrors.current.push(error);

                if (registrationErrors.current.length < 5) {
                    logger.info('Retrying push notification registration: ' + error);
                    setTimeout(PushNotifications.register, 10000);
                } else {
                    logger.error(`Push notifications registration failed ${registrationErrors.current.length} times. Errors: ${JSON.stringify(registrationErrors.current)}`);
                }

            }
        );

        // invoked when a notification is received while the app is in the foreground
        PushNotifications.addListener('pushNotificationReceived',
            (notification: PushNotificationSchema) => {
                logger.info('Push notification received in app: ' + JSON.stringify(notification));

                const { title, body, data } = notification;
                const { type, resourceToReload, gotoAndReload, goto, campaignId, durationMS: durationMSString, doNotShowToast } = data as IAPPushNotificationExtras;

                const buttonCaption = type === 'Recording Reminder' ? 'Start' : 'Open';

                const maybeShowToast = () => {
                    if (doNotShowToast){
                        return;
                    }

                    const url = gotoAndReload || goto;
                    const buttons: IInAppNotificationButton[] = [];

                    if (url && history.getCurrentLocation().pathname !== url) {
                        buttons.push({ text: buttonCaption, handler: () => {

                            mixpanel.track('Push Notification Opened', () => ({ from: 'Inside App', type, title, body, goto: url, campaignId }));

                            if (gotoAndReload && !resourceToReload && appState.isLoaded()) {
                                reloadApp(url);
                            } else {
                                maybeNavigate(url);
                            }
                        } });
                    }

                    const duration = Number.isNaN(durationMSString) ? undefined : Number(durationMSString);

                    inAppNotification.showNotification({
                        header: notification.title,
                        message: notification.body || '',
                        buttons,
                        duration
                    });
                };

                if (resourceToReload) {
                    events.emit('PushNotificationListener.reload-required', {
                        resource: resourceToReload,
                        onReload: maybeShowToast
                    });
                } else {
                    maybeShowToast();
                }
            }
        );

        // invoked when user taps on ios notification while app is in the background or closed
        PushNotifications.addListener('pushNotificationActionPerformed',
            (action: ActionPerformed) => {
                logger.info('Push notification clicked outside app: ' + JSON.stringify(action.notification));

                const { title, body, data } = action.notification;
                const { type, resourceToReload, gotoAndReload, goto, campaignId } = data as IAPPushNotificationExtras;
                const url = gotoAndReload || goto;

                mixpanel.track('Push Notification Opened', () => ({ from: 'Outside App', type, title, body, goto: url, campaignId }));

                // Dismiss any open recording modals immediately so they don't block the notification's destination
                if (url) {
                    events.emit(PUSH_NOTIFICATION_NAVIGATING);
                }

                if (resourceToReload) {
                    events.emit('PushNotificationListener.reload-required', {
                        resource: resourceToReload,
                        onReload: maybeNavigate(url)
                    });
                } else if (gotoAndReload && appState.isLoaded()) {
                    reloadApp(gotoAndReload);
                } else {
                    maybeNavigate(url);
                }
            }
        );

        // on android, we need a high-importance channel, so we can send
        // notifications that pop up and show on lock screen
        if (deviceInfo.platform === 'android') {
            PushNotifications.listChannels().then(result => {
                if (!result.channels?.find(c => c.id === 'high-importance')) {
                    PushNotifications.createChannel({
                        id: 'high-importance',
                        name: 'High Priority',
                        importance: 5
                    });
                }
            });
        }

        // Always trigger registration on app start to get the latest token and timezone
        // If user has not yet granted permission, expect this to fail
        PushNotifications.register();

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Register on app foreground to update timezone when user travels or changes settings
    useEffect(() => {
        if (!['ios', 'android'].includes(deviceInfo.platform)) {
            return;
        }

        const unsubscribe = events.on(APP_FOREGROUNDED, () => {
            PushNotifications.register();
        });

        return unsubscribe;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};
